/**
 * @module ui.header
 * @version 1.0.0
 * @description Peek Smart Header with contextual scroll progress bar.
 *
 * v1.1.0 - REFACTOR: Server-rendered progress bar (progress-bar component)
 *         - Removed: #createProgressBar(), #removeProgressBar() (DOM creation)
 *         - Changed: mount() queries existing DOM instead of creating
 *         - Changed: --header-progress → --progress--value
 *         - Progress bar now rendered in theme.liquid
 * 
 * Asymmetric hide/reveal: hiding is deliberate (distance + dwell + velocity),
 * revealing is instant. A progress bar appears only when the header is hidden,
 * using the freed top edge for scroll orientation.
 *
 * @element liftr-header
 *
 * @fires liftr:header:hidden   - Header slid out of view
 * @fires liftr:header:revealed - Header slid back into view
 *
 * @attr {string} liftr-state - State tokens (hidden, touching)
 *
 * CSS Custom Properties (read by JS):
 * - --header-enabled:      Toggle behavior (default: true)
 * - --header-hide-distance: Scroll distance before hide (default: 80)
 * - --header-dwell-time:   Sustained scroll duration in ms (default: 300)
 * - --header-top-zone:     Always-visible zone in px (default: 100)
 * - --fx-morph-duration:   Transition timing (from FX Set)
 * - --fx-morph-ease:       Transition easing (from FX Set)
 *
 * CSS Custom Properties (set by JS):
 * - --header-progress: Scroll progress 0–1
 *
 * @example
 * <liftr-header role="banner" class="page-header color-scheme-dark">
 *   {% sections 'header' %}
 * </liftr-header>
 */

import { LiftrModule } from './core.module.js';
import { FX, ATTRIBUTES } from './core.liftr.js';

/* ═══════════════════════════════════════════════════════════════════════════
 * MODULE CONSTANTS
 * ═══════════════════════════════════════════════════════════════════════════ */

/**
 * Role constants for DOM queries.
 * @type {Readonly<Record<string, string>>}
 */
const ROLES = Object.freeze({
  PROGRESS: 'header-progress',
});

/**
 * State tokens for liftr-state attribute.
 * @type {Readonly<Record<string, string>>}
 */
const STATES = Object.freeze({
  HIDDEN:   'hidden',
  TOUCHING: 'touching',
});

/**
 * Event action names (auto-prefixed with liftr:header:).
 * @type {Readonly<Record<string, string>>}
 */
const EVENTS = Object.freeze({
  HIDDEN:   'hidden',
  REVEALED: 'revealed',
});

/**
 * Settings tokens for liftr-settings attribute.
 * @type {Readonly<Record<string, string>>}
 */
const SETTINGS = Object.freeze({});

/**
 * CSS Custom Properties set by JavaScript.
 * @type {Readonly<Record<string, string>>}
 */
const PROPERTIES = Object.freeze({
  PROGRESS: '--header-progress',
});

/**
 * CSS schema for watchSettings() — properties JS reads from CSS.
 * @private
 */
const CSS_SCHEMA = Object.freeze({
  hideDistance: { type: 'number', default: 80 },
  dwellTime:   { type: 'number', default: 300 },
  topZone:     { type: 'number', default: 100 },
});

/**
 * Internal timing constants.
 * @private
 */
const TIMING = Object.freeze({
  VELOCITY_MAX:      60,   // px/frame — ignore fast flicks
  REVEAL_THRESHOLD:  2,    // px — minimum upward scroll to reveal
});

/**
 * CSS class constants.
 * @private
 */
const CLASSES = Object.freeze({
  VISIBLE: 'is-visible',
});

/* ═══════════════════════════════════════════════════════════════════════════
 * COMPONENT
 * ═══════════════════════════════════════════════════════════════════════════ */

class LiftrHeader extends LiftrModule {

  /* ─────────────────────────────────────────────────────────────────────────
   * STATIC PROPERTIES
   * ───────────────────────────────────────────────────────────────────────── */

  static NAME = 'header';

  static DEFAULTS = {};

  /* ─────────────────────────────────────────────────────────────────────────
   * PRIVATE FIELDS
   * ───────────────────────────────────────────────────────────────────────── */

  /** @type {HTMLElement|null} Progress bar element */
  #progressBar = null;

  /** @type {number} Last known scrollY */
  #lastScrollY = 0;

  /** @type {number} Accumulated downward scroll distance */
  #scrollDistance = 0;

  /** @type {number|null} Dwell timer ID */
  #dwellTimer = null;

  /** @type {boolean} Whether hide conditions (distance) have been met */
  #distanceMet = false;

  /** @type {boolean} Header is currently hidden */
  #isHidden = false;

  /** @type {boolean} Touch is active (lock state changes) */
  #isTouching = false;

  /** @type {number|null} Progress bar show delay timer */
  #progressTimer = null;

  /** @type {number} Previous frame timestamp for velocity */
  #prevTimestamp = 0;

  /** @type {number} Scroll velocity (px/frame) */
  #velocity = 0;

  /** @type {Object|null} Current CSS settings from watchSettings */
  #settings = null;

  /** @type {AbortController|null} Listener cleanup controller */
  #ac = null;

  /** @type {number} Cached page height for progress calculation */
  #pageHeight = 0;

  /** @type {number} Cached CSS transition duration in ms (Single Source of Truth) */
  #transitionMs = 300;

  /* ─────────────────────────────────────────────────────────────────────────
   * LIFECYCLE
   * ───────────────────────────────────────────────────────────────────────── */

  mount() {

    this.#progressBar = document.querySelector(
      `[${ATTRIBUTES.ROLE}~="${ROLES.PROGRESS}"]`
    );

    // Cache initial values
    this.#lastScrollY = window.scrollY;
    this.#updatePageHeight();

    // Watch CSS settings for responsive enable/disable
    this.watchSettings('header', {
      enabled: '--header-enabled',
      css: CSS_SCHEMA,
      resize: true,
    }, {
      onEnable:  (settings) => this.#enable(settings),
      onDisable: () => this.#disable(),
      onChange:   (changes) => this.#handleChanges(changes),
    });
  }

  unmount() {
    this.#teardownListeners();
    this.#hideProgress();
    this.#clearTimers();
    this.#progressBar = null;
    this.state.clear();
    super.unmount();
  }

  /* ─────────────────────────────────────────────────────────────────────────
   * SETTINGS CALLBACKS
   * ───────────────────────────────────────────────────────────────────────── */

  /**
   * Enable header behavior with CSS settings.
   * Called on responsive enable (disabled→enabled transition).
   * @param {Object} settings
   */
  #enable(settings) {
    this.#settings = settings;
    this.#cacheTransitionDuration();
    this.#bindListeners();
  }

  /**
   * Disable header behavior — reset to visible.
   */
  #disable() {
    this.#teardownListeners();
    this.#clearTimers();
    this.#show(true);
    this.#settings = null;
  }

  /**
   * Handle CSS settings changes.
   * Also handles initial setup (initial: true) since watchSettings
   * only fires onEnable on disabled→enabled transitions, not on first mount.
   * @param {Object} changes
   */
  #handleChanges(changes) {
    // Initial mount: treat as enable
    if (changes.initial) {
      this.#settings = changes;
      this.#cacheTransitionDuration();
      this.#bindListeners();
      return;
    }

    if (!this.#settings) return;
    if (changes.css) {
      this.#settings.css = changes.css;
    }
  }

  /* ─────────────────────────────────────────────────────────────────────────
   * PROGRESS BAR
   * ───────────────────────────────────────────────────────────────────────── */

  /**
   * Show progress bar with delay after header hides.
   * Delay matches CSS transition duration (Single Source of Truth).
   */
  #showProgress() {
    this.#clearProgressTimer();
    this.#progressTimer = setTimeout(() => {
      this.#progressBar?.classList.add(CLASSES.VISIBLE);
      this.#progressTimer = null;
    }, this.#transitionMs);
  }

  /**
   * Hide progress bar instantly (before header reveals).
   */
  #hideProgress() {
    this.#clearProgressTimer();
    this.#progressBar?.classList.remove(CLASSES.VISIBLE);
  }

  /**
   * Update progress bar value based on scroll position.
   * Sets --progress--value (0–1) consumed by progress-bar_fill translateX.
   */
  #updateProgress() {
    if (!this.#progressBar) return;
    const progress = this.#pageHeight > 0
      ? Math.min(window.scrollY / this.#pageHeight, 1)
      : 0;
    this.#progressBar.style.setProperty(PROPERTIES.PROGRESS, progress.toFixed(4));
  }

  /* ─────────────────────────────────────────────────────────────────────────
   * SCROLL LOGIC
   * ───────────────────────────────────────────────────────────────────────── */

  /**
   * Main scroll handler — called on every scroll frame via passive listener.
   */
  #onScroll = () => {
    if (!this.#settings) return;

    const currentY = window.scrollY;
    const delta = currentY - this.#lastScrollY;
    const { hideDistance, topZone } = this.#settings.css;

    // Update progress bar on every scroll
    this.#updateProgress();

    // Calculate velocity
    const now = performance.now();
    const frameDelta = now - this.#prevTimestamp;
    this.#velocity = frameDelta > 0 ? Math.abs(delta) / (frameDelta / 16.67) : 0;
    this.#prevTimestamp = now;

    // ── Top zone: always visible ──
    if (currentY < topZone) {
      this.#resetTracking();
      if (this.#isHidden) this.#show();
      this.#lastScrollY = currentY;
      return;
    }

    // ── Touch lock: no state changes during active touch ──
    if (this.#isTouching) {
      this.#lastScrollY = currentY;
      return;
    }

    // ── Scrolling UP: instant reveal ──
    if (delta < -TIMING.REVEAL_THRESHOLD) {
      this.#resetTracking();
      if (this.#isHidden) this.#show();
      this.#lastScrollY = currentY;
      return;
    }

    // ── Scrolling DOWN: deliberate hide ──
    if (delta > 0) {
      // Ignore fast flicks
      if (this.#velocity > TIMING.VELOCITY_MAX) {
        this.#lastScrollY = currentY;
        return;
      }

      // Accumulate distance
      this.#scrollDistance += delta;

      // Check distance threshold
      if (this.#scrollDistance >= hideDistance && !this.#distanceMet) {
        this.#distanceMet = true;
        this.#startDwellTimer();
      }
    }

    this.#lastScrollY = currentY;
  };

  /**
   * Start dwell timer — header hides only after sustained scrolling.
   */
  #startDwellTimer() {
    this.#clearDwellTimer();
    const { dwellTime } = this.#settings.css;

    this.#dwellTimer = setTimeout(() => {
      // Verify still scrolling down when timer fires
      if (this.#distanceMet && !this.#isHidden && !this.#isTouching) {
        this.#hide();
      }
      this.#dwellTimer = null;
    }, dwellTime);
  }

  /**
   * Reset all tracking state (on direction change or top zone).
   */
  #resetTracking() {
    this.#scrollDistance = 0;
    this.#distanceMet = false;
    this.#clearDwellTimer();
  }

  /* ─────────────────────────────────────────────────────────────────────────
   * SHOW / HIDE
   * ───────────────────────────────────────────────────────────────────────── */

  /**
   * Hide the header — slide up.
   */
  #hide() {
    if (this.#isHidden) return;
    this.#isHidden = true;

    this.state.add(STATES.HIDDEN);
    this.emit(EVENTS.HIDDEN);

    // Choreography: progress bar appears after delay
    this.#showProgress();
  }

  /**
   * Reveal the header — slide down.
   * @param {boolean} [silent=false] - If true, don't emit event (used during disable)
   */
  #show(silent = false) {
    if (!this.#isHidden) return;
    this.#isHidden = false;

    // Choreography: progress bar hides first
    this.#hideProgress();

    this.state.remove(STATES.HIDDEN);
    this.#resetTracking();

    if (!silent) {
      this.emit(EVENTS.REVEALED);
    }
  }

  /* ─────────────────────────────────────────────────────────────────────────
   * TOUCH HANDLING
   * ───────────────────────────────────────────────────────────────────────── */

  /**
   * Lock state changes during active touch.
   */
  #onTouchStart = () => {
    this.#isTouching = true;
    this.state.add(STATES.TOUCHING);
  };

  /**
   * Unlock state changes when touch ends.
   */
  #onTouchEnd = () => {
    this.#isTouching = false;
    this.state.remove(STATES.TOUCHING);
    // Reset tracking so next scroll starts fresh
    this.#resetTracking();
  };

  /* ─────────────────────────────────────────────────────────────────────────
   * EVENT BINDING
   * ───────────────────────────────────────────────────────────────────────── */

  /**
   * Bind scroll and touch listeners.
   */
  #bindListeners() {
    this.#teardownListeners();

    this.#ac = new AbortController();
    const opts = { passive: true, signal: this.#ac.signal };

    window.addEventListener('scroll', this.#onScroll, opts);
    window.addEventListener('touchstart', this.#onTouchStart, opts);
    window.addEventListener('touchend', this.#onTouchEnd, opts);
    window.addEventListener('touchcancel', this.#onTouchEnd, opts);
    window.addEventListener('resize', this.#onResize, opts);

    this.addDisposable(() => this.#teardownListeners());
  }

  /**
   * Teardown all listeners via AbortController.
   */
  #teardownListeners() {
    this.#ac?.abort();
    this.#ac = null;
  }

  /**
   * Handle resize — update cached page height.
   */
  #onResize = () => {
    this.#updatePageHeight();
  };

  /* ─────────────────────────────────────────────────────────────────────────
   * UTILITIES
   * ───────────────────────────────────────────────────────────────────────── */

  /**
   * Cache page height for progress calculation.
   */
  #updatePageHeight() {
    this.#pageHeight = document.documentElement.scrollHeight - window.innerHeight;
  }

  /**
   * Read CSS transition duration from FX Set.
   * CSS is Single Source of Truth — JS reads via getCssDuration() (LiftrModule).
   */
  #cacheTransitionDuration() {
    this.#transitionMs = this.getCssDuration(FX.PROPERTIES.MORPH_DURATION, 300);
  }

  /**
   * Clear all timers.
   */
  #clearTimers() {
    this.#clearDwellTimer();
    this.#clearProgressTimer();
  }

  /**
   * Clear dwell timer.
   */
  #clearDwellTimer() {
    if (this.#dwellTimer !== null) {
      clearTimeout(this.#dwellTimer);
      this.#dwellTimer = null;
    }
  }

  /**
   * Clear progress show timer.
   */
  #clearProgressTimer() {
    if (this.#progressTimer !== null) {
      clearTimeout(this.#progressTimer);
      this.#progressTimer = null;
    }
  }

  /* ─────────────────────────────────────────────────────────────────────────
   * PUBLIC API
   * ───────────────────────────────────────────────────────────────────────── */

  /**
   * Whether the header is currently hidden.
   * @returns {boolean}
   */
  get isHidden() {
    return this.#isHidden;
  }

  /**
   * Programmatically show the header.
   */
  show() {
    this.#show();
  }

  /**
   * Programmatically hide the header.
   */
  hide() {
    this.#hide();
  }
}

/* ═══════════════════════════════════════════════════════════════════════════
 * REGISTRATION
 * ═══════════════════════════════════════════════════════════════════════════ */

customElements.define('liftr-header', LiftrHeader);

export { LiftrHeader, ROLES, STATES, EVENTS, SETTINGS };
export default LiftrHeader;